import { __ } from '@wordpress/i18n';
import { useBlockProps, useInnerBlocksProps } from '@wordpress/block-editor';
import Inspector from './InspectorControls';
import { getWrapperClasses, getWrapperStyles } from './styles';
import { getDefaultAvatarAttributes } from '../_shared/default-avatars';
import './style.scss';

/**
 * Team Member — a person's photo, name, role, bio and social links.
 *
 * Composed rather than monolithic: the photo is `brandy/avatar` (which reads
 * the `brandy/avatar*` context this block provides) and the links are
 * `brandy/social-icons`. Name/role/bio are core blocks — this is author-entered
 * static content, so core gives full typography and colour controls for free.
 * The Testimonials suite uses dedicated child blocks instead because those map
 * to queried review data.
 */
/**
 * Inserted with sample content rather than empty placeholders.
 *
 * An empty Team Member reads as a broken block: the avatar is a bare circle,
 * the name and role are invisible until clicked, and the card's spacing and
 * alignment controls have nothing to act on. Filled-in text makes the layout
 * legible the moment it lands, and overwriting it is one selection — the same
 * trade Social Icons already makes by seeding three services.
 *
 * The strings mirror block.json's `example`, so the inserter preview and the
 * block an author actually gets are the same thing.
 */
const TEMPLATE = [
  // Sample face rather than an initials tile, and the same first person the
  // Avatar Group seeds — see _shared/default-avatars.js for why it is a URL.
  ['brandy/avatar', getDefaultAvatarAttributes(0)],
  [
    'core/heading',
    {
      level: 3,
      content: __('Ava Bennett', 'brandy-blocks'),
      placeholder: __('Name', 'brandy-blocks'),
      className: 'brandy-team-member__name',
    },
  ],
  [
    'core/paragraph',
    {
      content: __('Head of Design', 'brandy-blocks'),
      placeholder: __('Role', 'brandy-blocks'),
      className: 'brandy-team-member__role',
    },
  ],
  [
    'core/paragraph',
    {
      content: __(
        'Ava turns rough ideas into interfaces people can use without a manual.',
        'brandy-blocks'
      ),
      placeholder: __('Short bio', 'brandy-blocks'),
      className: 'brandy-team-member__bio',
    },
  ],
  [
    'brandy/social-icons',
    { shape: 'none', colorMode: 'inherit', iconSize: 18 },
    // Named here rather than left to Social Icons' own seed: a person's links
    // are LinkedIn-first, not a brand's Facebook page. Each carries a URL
    // because render.php drops icons that have none.
    [
      ['brandy/social-icon', { service: 'linkedin', url: 'https://linkedin.com/' }],
      ['brandy/social-icon', { service: 'x', url: 'https://x.com/' }],
      ['brandy/social-icon', { service: 'instagram', url: 'https://instagram.com/' }],
    ],
  ],
];

export default function Edit({ attributes, setAttributes, clientId }) {
  const blockProps = useBlockProps({
    className: getWrapperClasses(attributes),
    style: getWrapperStyles(attributes),
  });

  // Unlocked: authors routinely need to drop in a quote, a button, or a second
  // paragraph. The template is a starting point, not a cage.
  const innerBlocksProps = useInnerBlocksProps(blockProps, {
    template: TEMPLATE,
    templateLock: false,
  });

  return (
    <>
      <Inspector
        attributes={attributes}
        setAttributes={setAttributes}
        clientId={clientId}
      />
      <div {...innerBlocksProps} />
    </>
  );
}
